import React, { useEffect, useRef } from 'react';
import { Phone, MessageSquare, Mail, Globe, MapPin } from 'lucide-react';

/**
 * Contact popup shown for non-affiliated cards. Opens when the user taps
 * Book Now on a card whose `non_affiliated` flag is true. Renders only the
 * fields that are filled in (everything is optional).
 *
 * The dim overlay is anchored to the experience card (the modal lives
 * inside the card's transform context). It uses `rounded-2xl` so the
 * faded zone matches the card's rounded shape.
 *
 * Dismisses on:
 *   - ESC key
 *   - any scroll / touchmove
 *   - tap on the backdrop. We rely *only* on `onClick` (not touchstart/
 *     mousedown) so the backdrop element stays mounted long enough to
 *     intercept the synthesized click on mobile — otherwise the click
 *     would fall through to the underlying Book Now button and reopen us.
 */
export function NonAffiliatedModal({
  open,
  onClose,
  text,
  phone,
  email,
  web,
  location,
}) {
  const contentRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    const onScroll = () => onClose();

    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('touchmove', onScroll, { passive: true });
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('touchmove', onScroll);
    };
  }, [open, onClose]);

  if (!open) return null;

  // Clean digits + leading "+" only.
  const cleanPhone = (phone || '').replace(/[^\d+]/g, '');
  // Show WhatsApp + SMS only for Spanish mobile numbers (+34 followed by 6 or 7).
  const isSpanishMobile = /^\+34[67]/.test(cleanPhone);
  const waPhone = cleanPhone.replace(/^\+/, '');

  const mapsUrl = location
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`
    : null;
  const webUrl = web && !/^https?:\/\//i.test(web) ? `https://${web}` : web;

  // Backdrop click — fires on the topmost element under the tap, which is
  // always this backdrop (z-index above the card). Close only on outside
  // clicks, and stop propagation so the underlying Book Now button never
  // sees the event on mobile.
  const handleBackdropClick = (e) => {
    if (contentRef.current && contentRef.current.contains(e.target)) return;
    e.preventDefault();
    e.stopPropagation();
    onClose();
  };

  return (
    <div
      className="absolute inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm rounded-2xl"
      data-testid="non-affiliated-modal"
      onClick={handleBackdropClick}
    >
      <div
        ref={contentRef}
        className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 pt-7 pb-6">
          {text && (
            <p
              className="text-sm font-medium text-[#1a4d7a] mb-5 whitespace-pre-line"
              data-testid="non-affiliated-text"
            >
              {text}
            </p>
          )}

          <div className="space-y-3">
            {phone && (
              <div className="flex items-center gap-3">
                <a
                  href={`tel:${cleanPhone}`}
                  className="flex-1 flex items-center gap-2 text-sm font-semibold text-[#1a4d7a] underline underline-offset-2 hover:text-[#3c84ff]"
                >
                  <Phone className="h-4 w-4 text-[#3c84ff] no-underline" />
                  {phone}
                </a>
                {isSpanishMobile && (
                  <div className="flex items-center gap-1.5 shrink-0">
                    <a
                      href={`https://wa.me/${waPhone}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="WhatsApp"
                      className="h-9 w-9 rounded-full bg-[#25D366] text-white flex items-center justify-center hover:bg-[#1ebe57] transition-colors"
                      data-testid="non-affiliated-whatsapp-btn"
                    >
                      <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current">
                        <path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 0 0 1.599 5.317l-.999 3.648 3.889-1.664zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.371-.025-.52-.075-.149-.669-1.611-.916-2.206-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.296-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
                      </svg>
                    </a>
                    <a
                      href={`sms:${cleanPhone}`}
                      aria-label="SMS"
                      className="h-9 w-9 rounded-full bg-[#1a4d7a] text-white flex items-center justify-center hover:bg-[#143b5e] transition-colors"
                      data-testid="non-affiliated-sms-btn"
                    >
                      <MessageSquare className="h-4 w-4" />
                    </a>
                  </div>
                )}
              </div>
            )}

            {email && (
              <a
                href={`mailto:${email}`}
                className="flex items-center gap-2 text-sm font-semibold text-[#1a4d7a] underline underline-offset-2 hover:text-[#3c84ff]"
                data-testid="non-affiliated-email-link"
              >
                <Mail className="h-4 w-4 text-[#3c84ff] no-underline" />
                {email}
              </a>
            )}

            {webUrl && (
              <a
                href={webUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm font-semibold text-[#1a4d7a] underline underline-offset-2 hover:text-[#3c84ff] break-all"
                data-testid="non-affiliated-web-link"
              >
                <Globe className="h-4 w-4 text-[#3c84ff] no-underline shrink-0" />
                {web}
              </a>
            )}

            {mapsUrl && (
              <a
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm font-semibold text-[#1a4d7a] underline underline-offset-2 hover:text-[#3c84ff]"
                data-testid="non-affiliated-location-link"
              >
                <MapPin className="h-4 w-4 text-[#3c84ff] no-underline" />
                {location}
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
